import React, { useCallback, memo } from 'react';
import { Box, TextField, IconButton } from '@mui/material';
import { Send } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { InputPanelVariantType, InputPanelProps } from '../types';

const InputContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  maxWidth: '800px',
  backgroundColor: theme.palette.background.paper,
  borderRadius: '16px',
  padding: theme.spacing(1, 2),
  boxShadow: theme.shadows[3],
  border: `1px solid #e0e0e0`,
  gap: theme.spacing(1),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
   flex: 1,
  '& .MuiOutlinedInput-root': {
    border: 'none',
    backgroundColor: 'transparent',
    '& fieldset': {
      border: 'none',
    },
    '&:hover fieldset': {
      border: 'none',
    },
    '&.Mui-focused fieldset': {
      border: 'none',
    },
  },
  '& .MuiInputBase-input': {
    fontSize: '0.95rem',
    padding: theme.spacing(1.5, 0),
    color: theme.palette.input?.main || theme.palette.text.primary,
    '&::placeholder': {
      color: theme.palette.inputPlaceholder?.main || theme.palette.text.secondary,
      opacity: 1,
    },
  },
}));

const SendButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.secondary.main,
  width: '40px',
  height: '40px',
  minWidth: '40px',
  minHeight: '40px',
  borderRadius: '50%',
  '&:hover': {
    backgroundColor: theme.palette.secondary.dark,
  },
  '&:disabled': {
    backgroundColor: theme.palette.disabled?.primary,
    color: theme.palette.disabled?.secondary,
  },
}));

export const InputPanel: React.FC<InputPanelProps> = memo(({
  text = '',
  variant = 'entry',
  placeholder = 'Type your message...',
  onSend,
  disabled = false,
  multiline = true,
  maxRows = 3,
}) => {
  const [inputText, setInputText] = React.useState(text);

  // Update local state when text prop changes
  React.useEffect(() => {
    setInputText(text);
  }, [text]);

  const handleSend = useCallback(() => {
    if (inputText.trim() && !disabled) {
      onSend(inputText.trim());
      setInputText('');
    }
  }, [inputText, disabled, onSend]);

  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !disabled) {
      e.preventDefault();
      handleSend();
    }
  }, [disabled, handleSend]);

  return (
    <InputContainer>
      <StyledTextField
        value={inputText}
        onChange={(e) => setInputText(e.target.value)}
        onKeyPress={handleKeyPress}
        placeholder={placeholder}
        disabled={disabled}
        size="small"
        multiline={multiline}
        maxRows={maxRows}
      />
      <SendButton
        onClick={handleSend}
        disabled={!inputText.trim() || disabled}
        aria-label="Send"
      >
        <Send sx={{ fontSize: 22 }} />
      </SendButton>
    </InputContainer>
  );
});

InputPanel.displayName = 'InputPanel';

